import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// ==================== PARALLAX SYSTEM ====================

export function useParallaxHero() {
  const heroRef = useRef(null)
  const bgRef = useRef(null)
  const contentRef = useRef(null)
  const visualRef = useRef(null)

  useEffect(() => {
    if (!heroRef.current) return
    const ctx = gsap.context(() => {
      // Background layer - slowest
      gsap.to(bgRef.current, {
        y: 200,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        }
      })

      // Content - medium speed
      gsap.to(contentRef.current, {
        y: 80,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1.5,
        }
      })

      // Visual element - slightly faster for depth
      gsap.to(visualRef.current, {
        y: 40,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 2,
        }
      })
    }, heroRef.current)

    return () => ctx.revert()
  }, [])

  return { heroRef, bgRef, contentRef, visualRef }
}

// ==================== HERO ENTRANCE ANIMATION ====================

export function useHeroEntrance() {
  const heroRef = useRef(null)

  useEffect(() => {
    if (!heroRef.current) return
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

      tl.from('.hero-label', { y: 40, opacity: 0, duration: 0.8, delay: 0.2 })
        .from('.hero-title-line', { y: 80, opacity: 0, duration: 1.2, stagger: 0.15 }, '-=0.4')
        .from('.hero-description', { y: 30, opacity: 0, duration: 0.9 }, '-=0.7')
        .from('.hero-actions', { y: 30, opacity: 0, duration: 0.8 }, '-=0.5')
        .from('.hero-metric', { y: 20, opacity: 0, duration: 0.6, stagger: 0.1 }, '-=0.4')
    }, heroRef.current)

    return () => ctx.revert()
  }, [])

  return heroRef
}

// ==================== SCROLL REVEAL ====================

export function useScrollReveal() {
  const containerRef = useRef(null)

  useEffect(() => {
    if (!containerRef.current) return
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.reveal-up').forEach((el) => {
        gsap.from(el, {
          y: 60,
          opacity: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none',
          }
        })
      })

      gsap.utils.toArray('.reveal-left').forEach((el) => {
        gsap.from(el, {
          x: -60,
          opacity: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none',
          }
        })
      })

      gsap.utils.toArray('.reveal-right').forEach((el) => {
        gsap.from(el, {
          x: 60,
          opacity: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none',
          }
        })
      })
    }, containerRef.current)

    return () => ctx.revert()
  }, [])

  return containerRef
}

// ==================== STAGGER REVEAL ====================

export function useStaggerReveal(containerRef) {
  useEffect(() => {
    if (!containerRef.current) return

    const ctx = gsap.context(() => {
      gsap.utils.toArray('.stagger-item').forEach((el, i) => {
        gsap.from(el, {
          y: 50,
          opacity: 0,
          duration: 0.8,
          delay: i * 0.08,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 90%',
            toggleActions: 'play none none none',
          }
        })
      })
    }, containerRef.current)

    return () => ctx.revert()
  }, [containerRef])
}

// ==================== HORIZONTAL PARALLAX (for detail page) ====================

export function useHorizontalParallax() {
  const sectionRef = useRef(null)
  const trackRef = useRef(null)

  useEffect(() => {
    if (!sectionRef.current || !trackRef.current) return
    const ctx = gsap.context(() => {
      gsap.to(trackRef.current, {
        x: () => -(trackRef.current.scrollWidth - window.innerWidth + 80),
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: () => `+=${trackRef.current.scrollWidth - window.innerWidth + 80}`,
          pin: true,
          scrub: 1,
          anticipatePin: 1,
        }
      })
    }, sectionRef.current)

    return () => ctx.revert()
  }, [])

  return { sectionRef, trackRef }
}

// ==================== COUNT UP ====================

export function useCountUp(target, duration = 2000) {
  const ref = useRef(null)
  const [count, setCount] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            let start = null
            const step = (timestamp) => {
              if (!start) start = timestamp
              const progress = Math.min((timestamp - start) / duration, 1)
              const ease = 1 - Math.pow(1 - progress, 3)
              setCount(Math.floor(ease * target))
              if (progress < 1) requestAnimationFrame(step)
              else setCount(target)
            }
            requestAnimationFrame(step)
            observer.unobserve(el)
          }
        })
      },
      { threshold: 0.5 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [target, duration])

  return { ref, count }
}

// ==================== SCROLL VELOCITY TILT ====================

export function useScrollVelocityTilt() {
  const tiltRef = useRef(null)

  useEffect(() => {
    const el = tiltRef.current
    if (!el) return

    let lastScroll = 0
    let currentTilt = 0

    const handleScroll = () => {
      const velocity = window.scrollY - lastScroll
      const targetTilt = Math.max(-2, Math.min(2, velocity * 0.01))
      currentTilt += (targetTilt - currentTilt) * 0.1
      el.style.transform = `perspective(1000px) rotateX(${currentTilt}deg)`
      lastScroll = window.scrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return tiltRef
}

// ==================== MAGNETIC HOVER ====================

export function useMagneticHover() {
  const magneticRef = useRef(null)

  useEffect(() => {
    const el = magneticRef.current
    if (!el) return

    const handleMove = (e) => {
      const rect = el.getBoundingClientRect()
      const x = e.clientX - rect.left - rect.width / 2
      const y = e.clientY - rect.top - rect.height / 2
      el.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`
    }

    const handleLeave = () => {
      el.style.transform = 'translate(0, 0)'
    }

    el.addEventListener('mousemove', handleMove)
    el.addEventListener('mouseleave', handleLeave)
    return () => {
      el.removeEventListener('mousemove', handleMove)
      el.removeEventListener('mouseleave', handleLeave)
    }
  }, [])

  return magneticRef
}
